import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../AuthContext';
import { canManageUsers } from '../permissions';
import '../styles/users-admin.css';

const ROLE_OPTIONS = [
  'admin', 'executive', 'manager', 'spx', 'pm', 'estimator', 'bid_coordinator', 'viewer',
];

const ROLE_LABELS = {
  admin: 'Administrator',
  executive: 'Executive',
  manager: 'Manager',
  spx: 'Sr. Project Executive',
  pm: 'Project Manager',
  estimator: 'Estimator',
  bid_coordinator: 'Estimating Coordinator',
  viewer: 'Viewer',
};

function roleLabel(role) {
  return ROLE_LABELS[role] || role || '-';
}

// Permission is the access tier, independent of the job-title Role above.
const PERMISSION_OPTIONS = ['admin', 'manager', 'user', 'viewer'];
const PERMISSION_LABELS = {
  admin: 'Admin — full access incl. user management',
  manager: 'Manager — edit/manage any project',
  user: 'User — edit projects they are assigned to',
  viewer: 'Viewer — read only',
};
function permissionLabel(p) {
  return ({ admin: 'Admin', manager: 'Manager', user: 'User', viewer: 'Viewer' })[p] || p || '-';
}

export default function UsersAdmin() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [tab, setTab] = useState('users');
  const [sortCol, setSortCol] = useState('DisplayName');
  const [sortDir, setSortDir] = useState('asc');
  const [showArchived, setShowArchived] = useState(false);

  function handleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getAdminUsers();
      setUsers(data.users || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    // Archived (disabled) users are hidden unless "Show archived" is on.
    const roster = showArchived ? users : users.filter(u => u.IsActive);
    const base = !q ? roster : roster.filter(u =>
      (u.DisplayName || '').toLowerCase().includes(q)
      || (u.Email || '').toLowerCase().includes(q)
      || (u.Username || '').toLowerCase().includes(q)
      || (u.Title || '').toLowerCase().includes(q)
    );

    const getVal = (u) => {
      switch (sortCol) {
        case 'DisplayName': return (u.DisplayName || '').toLowerCase();
        case 'Email':       return (u.Email || '').toLowerCase();
        case 'Role':        return (u.Role || '').toLowerCase();
        case 'Permission':  return (u.Permission || '').toLowerCase();
        case 'IsActive':    return u.IsActive ? 0 : 1;
        case 'LastLoginAt': return u.LastLoginAt || '';
        default:            return '';
      }
    };

    return [...base].sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [users, search, sortCol, sortDir, showArchived]);

  async function toggleArchive(u) {
    const archiving = !!u.IsActive;
    if (archiving && !window.confirm(`Archive ${u.DisplayName}? They'll be hidden and can't be assigned to projects.`)) return;
    try {
      await api.archiveUser(u.UserId, archiving);
      load();
    } catch (e) {
      alert('Failed: ' + e.message);
    }
  }

  const sortInd = (col) => sortCol === col ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : '';

  const stats = useMemo(() => {
    let active = 0, disabled = 0, admins = 0;
    users.forEach(u => {
      if (u.IsActive) active++; else disabled++;
      if (u.IsAdmin) admins++;
    });
    return { total: users.length, active, disabled, admins };
  }, [users]);

  if (!canManageUsers(user)) {
    return (
      <div className="page-content">
        <div className="empty-state">
          <h3>Admin only</h3>
          <p>This page is restricted to administrators.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="top-bar">
        <div className="top-bar-left">
          <Link to="/settings" className="btn btn-secondary btn-sm" style={{ textDecoration: 'none' }}>&larr; Settings</Link>
          <h2>Users</h2>
        </div>
        <div className="top-bar-right">
          <input
            type="search"
            className="users-admin-search"
            placeholder="Filter by name, email, username..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <button className="btn btn-primary btn-sm" onClick={() => setCreating(true)}>+ New User</button>
          <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="page-content">
        <div className="ua-tabs">
          <button className={`ua-tab ${tab === 'users' ? 'ua-tab-active' : ''}`} onClick={() => setTab('users')}>Users</button>
          <button className={`ua-tab ${tab === 'audit' ? 'ua-tab-active' : ''}`} onClick={() => setTab('audit')}>Login Audit</button>
        </div>

        {tab === 'audit' && <LoginAuditLog />}

        {tab === 'users' && <>
        {error && <div className="users-admin-error">Failed to load users: {error}</div>}

        <div className="users-admin-stats">
          <StatPill label="Total" value={stats.total} />
          <StatPill label="Active" value={stats.active} />
          <StatPill label="Disabled" value={stats.disabled} tone={stats.disabled ? 'danger' : null} />
          <StatPill label="Admins" value={stats.admins} tone="warn" />
          <label style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#555', cursor: 'pointer' }}>
            <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />
            Show archived
          </label>
        </div>

        <div className="card" style={{ marginTop: '16px' }}>
          <div className="card-body" style={{ padding: 0 }}>
            {loading ? (
              <div className="empty-state">Loading...</div>
            ) : filtered.length === 0 ? (
              <div className="empty-state">No users match.</div>
            ) : (
              <table className="users-admin-table">
                <thead>
                  <tr>
                    <th className="sortable" onClick={() => handleSort('DisplayName')}>Name{sortInd('DisplayName')}</th>
                    <th className="sortable" onClick={() => handleSort('Email')}>Email{sortInd('Email')}</th>
                    <th className="sortable" onClick={() => handleSort('Role')}>Role{sortInd('Role')}</th>
                    <th className="sortable" onClick={() => handleSort('Permission')}>Permission{sortInd('Permission')}</th>
                    <th className="sortable" onClick={() => handleSort('IsActive')}>Status{sortInd('IsActive')}</th>
                    <th className="sortable" onClick={() => handleSort('LastLoginAt')}>Last Login{sortInd('LastLoginAt')}</th>
                    <th className="cell-actions" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(u => (
                    <tr key={u.UserId} className={!u.IsActive ? 'users-admin-row-disabled' : ''}>
                      <td>
                        <div>{u.DisplayName}</div>
                        <div className="cell-sub">{u.Title || u.Username}</div>
                      </td>
                      <td className="cell-mono">{u.Email}</td>
                      <td>
                        <span className={`role-pill role-${u.Role}`}>{roleLabel(u.Role)}</span>
                      </td>
                      <td>
                        <span className="role-pill" style={{ background: '#eef2ff', color: '#3730a3' }}
                          title="Access permission (independent of role)">{permissionLabel(u.Permission)}</span>
                        {u.IsAdmin && <span className="admin-flag">ADMIN</span>}
                      </td>
                      <td>
                        {u.IsActive
                          ? <span className="state-badge state-active">Active</span>
                          : <span className="state-badge state-disabled">Disabled</span>}
                        {u.MustResetPassword ? <span className="reset-flag">RESET</span> : null}
                      </td>
                      <td className="cell-muted">
                        {u.LastLoginAt
                          ? new Date(u.LastLoginAt).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
                          : 'Never'}
                      </td>
                      <td className="cell-actions">
                        <button className="btn btn-secondary btn-sm" onClick={() => setEditing(u)}>Edit</button>
                        {!u.IsSystemAccount && (
                          u.IsActive
                            ? <button className="btn btn-secondary btn-sm" onClick={() => toggleArchive(u)}>Archive</button>
                            : <button className="btn btn-secondary btn-sm" onClick={() => toggleArchive(u)}>Restore</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
        </>}
      </div>

      {editing && (
        <EditUserModal
          userData={editing}

          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {creating && (
        <CreateUserModal

          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); load(); }}
        />
      )}
    </>
  );
}

function StatPill({ label, value, tone }) {
  return (
    <div className={`users-stat-pill ${tone ? `users-stat-${tone}` : ''}`}>
      <span className="users-stat-value">{value}</span>
      <span className="users-stat-label">{label}</span>
    </div>
  );
}

function EditUserModal({ userData, onClose, onSaved }) {
  const [displayName, setDisplayName] = useState(userData.DisplayName);
  const [title, setTitle] = useState(userData.Title || '');
  const [email, setEmail] = useState(userData.Email);
  const [role, setRole] = useState(userData.Role);
  const [permission, setPermission] = useState(userData.Permission || 'viewer');
  const [isAdmin, setIsAdmin] = useState(!!userData.IsAdmin);
  const [isActive, setIsActive] = useState(!!userData.IsActive);
  const [mustReset, setMustReset] = useState(!!userData.MustResetPassword);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showResetPw, setShowResetPw] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [resetOnLogin, setResetOnLogin] = useState(true);

  async function save() {
    setSaving(true); setErr(null); setSuccess(null);
    try {
      await api.updateUser(userData.UserId, {
        displayName, title, email, role, permission, isAdmin, isActive,
        mustResetPassword: mustReset,
      });
      onSaved();
    } catch (e) {
      setErr(e.message); setSaving(false);
    }
  }

  async function resetPassword() {
    if (!newPassword) { setErr('Enter a new password.'); return; }
    setSaving(true); setErr(null); setSuccess(null);
    try {
      await api.resetUserPassword(userData.UserId, {
        newPassword, mustResetOnLogin: resetOnLogin,
      });
      setShowResetPw(false);
      setNewPassword('');
      setSuccess('Password reset successfully.');
      setSaving(false);
    } catch (e) {
      setErr(e.message); setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal users-admin-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit User</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <div className="ua-meta">
            <strong>{userData.DisplayName}</strong><br />
            Username: <code>{userData.Username}</code> &middot; Created {new Date(userData.CreatedAt).toLocaleDateString()}
            {userData.LastLoginAt && <> &middot; Last login {new Date(userData.LastLoginAt).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })} PST</>}
          </div>

          <div className="ua-form-row">
            <label>Display Name</label>
            <input className="ua-input" type="text" value={displayName} onChange={e => setDisplayName(e.target.value)} />
          </div>
          <div className="ua-form-row">
            <label>Email</label>
            <input className="ua-input" type="email" value={email} onChange={e => setEmail(e.target.value)} />
          </div>
          <div className="ua-form-row">
            <label>Title</label>
            <input className="ua-input" type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Job title (optional)" />
          </div>
          <div className="ua-form-row">
            <label>Role</label>
            <select className="ua-select" value={role} onChange={e => setRole(e.target.value)}>
              {ROLE_OPTIONS.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
            </select>
          </div>
          <div className="ua-hint" style={{ marginBottom: '16px' }}>The user's role is their job title and determines which project dropdowns they appear in (e.g. PMs appear in the Project Manager dropdown).</div>

          <div className="ua-form-row">
            <label>Permission</label>
            <select className="ua-select" value={permission} onChange={e => setPermission(e.target.value)}>
              {PERMISSION_OPTIONS.map(p => <option key={p} value={p}>{PERMISSION_LABELS[p]}</option>)}
            </select>
          </div>
          <div className="ua-hint" style={{ marginBottom: '16px' }}>Permission is access, independent of role. Managers/admins edit any project; Users edit projects they're assigned to as Estimator/EC; Viewers are read-only. Takes effect on the user's next login.</div>

          <label className="ua-check-row">
            <input type="checkbox" checked={isAdmin} onChange={e => setIsAdmin(e.target.checked)} />
            Administrator (full access to all settings and user management)
          </label>
          <label className="ua-check-row">
            <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
            Active (can sign in)
          </label>
          <label className="ua-check-row">
            <input type="checkbox" checked={mustReset} onChange={e => setMustReset(e.target.checked)} />
            Must reset password on next login
          </label>

          <div className="ua-divider" />

          {!showResetPw ? (
            <button className="btn btn-secondary btn-sm" onClick={() => setShowResetPw(true)}>Reset Password...</button>
          ) : (
            <>
              <div className="ua-form-row">
                <label>New Password</label>
                <input
                  className="ua-input"
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Min 8 chars, uppercase, lowercase, number, special"
                />
              </div>
              <label className="ua-check-row">
                <input type="checkbox" checked={resetOnLogin} onChange={e => setResetOnLogin(e.target.checked)} />
                Require password change on next login
              </label>
              <div className="ua-pw-actions">
                <button className="btn btn-primary btn-sm" onClick={resetPassword} disabled={saving}>Set Password</button>
                <button className="btn btn-secondary btn-sm" onClick={() => { setShowResetPw(false); setNewPassword(''); }}>Cancel</button>
              </div>
            </>
          )}

          {err && <div className="users-admin-error ua-feedback">{err}</div>}
          {success && <div className="users-admin-success">{success}</div>}
        </div>
        <div className="modal-footer">
          <div style={{ flex: 1 }} />
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateUserModal({ onClose, onSaved }) {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [title, setTitle] = useState('');
  const [role, setRole] = useState('viewer');
  const [permission, setPermission] = useState('viewer');
  const [password, setPassword] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [mustReset, setMustReset] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  async function save() {
    if (!email || !username || !displayName || !password) {
      setErr('Email, username, display name, and password are required.');
      return;
    }
    setSaving(true); setErr(null);
    try {
      await api.createUser({
        email, username, displayName, title, role, permission, password,
        isAdmin, mustResetPassword: mustReset,
      });
      onSaved();
    } catch (e) {
      setErr(e.message); setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal users-admin-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create New User</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <div className="ua-form-row">
            <label>Email *</label>
            <input className="ua-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="user@cmcorp.co" />
          </div>
          <div className="ua-form-row">
            <label>Username *</label>
            <input className="ua-input" type="text" value={username} onChange={e => setUsername(e.target.value)} placeholder="e.g. jsmith" />
            <div className="ua-hint">Short unique identifier, no spaces. Used for system references.</div>
          </div>
          <div className="ua-form-row">
            <label>Display Name *</label>
            <input className="ua-input" type="text" value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="Full name" />
          </div>
          <div className="ua-form-row">
            <label>Title</label>
            <input className="ua-input" type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Job title (optional)" />
          </div>
          <div className="ua-form-row">
            <label>Role</label>
            <select className="ua-select" value={role} onChange={e => setRole(e.target.value)}>
              {ROLE_OPTIONS.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
            </select>
          </div>
          <div className="ua-form-row">
            <label>Permission</label>
            <select className="ua-select" value={permission} onChange={e => setPermission(e.target.value)}>
              {PERMISSION_OPTIONS.map(p => <option key={p} value={p}>{PERMISSION_LABELS[p]}</option>)}
            </select>
          </div>
          <div className="ua-form-row">
            <label>Password *</label>
            <input className="ua-input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 8 chars, uppercase, lowercase, number, special" />
          </div>
          <label className="ua-check-row">
            <input type="checkbox" checked={isAdmin} onChange={e => setIsAdmin(e.target.checked)} />
            Administrator
          </label>
          <label className="ua-check-row">
            <input type="checkbox" checked={mustReset} onChange={e => setMustReset(e.target.checked)} />
            Must reset password on next login
          </label>

          {err && <div className="users-admin-error ua-feedback">{err}</div>}
        </div>
        <div className="modal-footer">
          <div style={{ flex: 1 }} />
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Creating...' : 'Create User'}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatPST(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('en-US', {
    timeZone: 'America/Los_Angeles',
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

function LoginAuditLog() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [emailFilter, setEmailFilter] = useState('');
  const [successFilter, setSuccessFilter] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit, offset };
      if (emailFilter) params.email = emailFilter;
      if (successFilter) params.success = successFilter;
      const data = await api.getLoginAudit(params);
      setEntries(data.entries || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error('Audit load error:', e);
    }
    setLoading(false);
  }, [emailFilter, successFilter, offset]);

  useEffect(() => { load(); }, [load]);

  function applyFilter() {
    setOffset(0);
  }

  return (
    <>
      <div className="ua-audit-controls">
        <input
          type="search"
          className="users-admin-search"
          placeholder="Filter by email..."
          value={emailFilter}
          onChange={e => { setEmailFilter(e.target.value); applyFilter(); }}
        />
        <select className="ua-audit-select" value={successFilter} onChange={e => { setSuccessFilter(e.target.value); applyFilter(); }}>
          <option value="">All attempts</option>
          <option value="true">Successful only</option>
          <option value="false">Failed only</option>
        </select>
        <span className="cell-muted">{total} total entries</span>
      </div>

      <div className="card" style={{ marginTop: '12px' }}>
        <div className="card-body" style={{ padding: 0 }}>
          {loading ? (
            <div className="empty-state">Loading...</div>
          ) : entries.length === 0 ? (
            <div className="empty-state">No login attempts recorded yet.</div>
          ) : (
            <table className="users-admin-table">
              <thead>
                <tr>
                  <th>Time (PST)</th>
                  <th>Email</th>
                  <th>User</th>
                  <th>Result</th>
                  <th>Reason</th>
                  <th>IP Address</th>
                  <th>Browser</th>
                </tr>
              </thead>
              <tbody>
                {entries.map(e => (
                  <tr key={e.LogId} className={!e.Success ? 'ua-audit-fail-row' : ''}>
                    <td className="cell-muted">{formatPST(e.CreatedAt)}</td>
                    <td className="cell-mono">{e.Email}</td>
                    <td>{e.DisplayName || '-'}</td>
                    <td>
                      {e.Success
                        ? <span className="state-badge state-active">OK</span>
                        : <span className="state-badge state-disabled">FAIL</span>}
                    </td>
                    <td className="cell-muted">{e.FailReason || '-'}</td>
                    <td className="cell-mono">{e.IpAddress || '-'}</td>
                    <td className="cell-muted ua-audit-ua">{e.UserAgent ? e.UserAgent.substring(0, 60) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {total > limit && (
        <div className="ua-audit-pager">
          <button className="btn btn-secondary btn-sm" disabled={offset === 0} onClick={() => setOffset(o => Math.max(0, o - limit))}>Previous</button>
          <span className="cell-muted">
            {offset + 1}–{Math.min(offset + limit, total)} of {total}
          </span>
          <button className="btn btn-secondary btn-sm" disabled={offset + limit >= total} onClick={() => setOffset(o => o + limit)}>Next</button>
        </div>
      )}
    </>
  );
}
